import Graphic from '@arcgis/core/Graphic';
import { Point, Polyline, Polygon } from '@arcgis/core/geometry';
import {
  CIMSymbol,
  SimpleMarkerSymbol,
  SimpleLineSymbol,
  SimpleFillSymbol,
} from '@arcgis/core/symbols';
import CIMSymbolLayer from '@arcgis/core/symbols/cim/CIMSymbolHelper';
import {
  SimpleMarkerSymbolProperties,
  SimpleLineSymbolProperties,
  SimpleFillSymbolProperties,
} from '@arcgis/core/properties/defaultUnit';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import Extent from '@arcgis/core/geometry/Extent';
import { CYAN_RGB_COLOR, DEFAULT_COLOR } from './colorizer';
import {
  GraphicStyle, linestringStyles, pointStyles, polygonStyles,
} from './graphic-styles';
import { GeometricShape } from '../types/geo';

export interface ArcgisJSON {
  x?: number,
  y?: number,
  spatialReference: SpatialReference,
  paths?: number[][][],
  rings?: number [][][],
}

type SymbolType = 'CIMPointSymbol' | 'CIMLineSymbol' | 'CIMPolygonSymbol';

const highlightedSymbol = (
  symbolType: SymbolType,
  styles: SimpleMarkerSymbolProperties | SimpleLineSymbolProperties | SimpleFillSymbolProperties,
): CIMSymbol => {
  const { color } = styles;
  const {
    r, g, b, a,
  } = color;

  const symbolLayers: CIMSymbolLayer = [
    {
      type: 'CIMSolidStroke',
      enable: true,
      width: 2.6,
      color: [r, g, b, 255],
    },
    {
      type: 'CIMSolidStroke',
      enable: true,
      width: 5,
      color: CYAN_RGB_COLOR,
    },
  ];

  if (symbolType === 'CIMPolygonSymbol') {
    const opacity = 255 * a;
    symbolLayers.push({
      type: 'CIMSolidFill',
      enable: true,
      color: [r, g, b, opacity],
      width: 0,
    });
  }

  return new CIMSymbol({
    data: {
      type: 'CIMSymbolReference',
      symbol: {
        type: symbolType,
        symbolLayers,
      },
    },
  });
};

export const symbolPoint = (color: string, style?: GraphicStyle): SimpleMarkerSymbol => {
  const styles = pointStyles(color, style);
  return new SimpleMarkerSymbol(styles);
};

export const symbolLineString = (color: string, style?: GraphicStyle): SimpleLineSymbol | CIMSymbol => {
  const styles = linestringStyles(color, style);

  if (style === GraphicStyle.TARGET_GEOMETRY) {
    const highlightedLineSymbol = highlightedSymbol('CIMLineSymbol', styles);
    return highlightedLineSymbol;
  }
  return new SimpleLineSymbol(styles);
};

export const symbolPolygon = (color: string, style?: GraphicStyle): SimpleFillSymbol | CIMSymbol => {
  const styles = polygonStyles(color, style);

  if (style === GraphicStyle.TARGET_GEOMETRY) {
    const customPolygonSymbol = highlightedSymbol('CIMPolygonSymbol', styles);
    return customPolygonSymbol;
  }
  return new SimpleFillSymbol(styles);
};

export const graphicPoint = (
  json: ArcgisJSON,
  color: string,
  style?: GraphicStyle,
  shape?: GeometricShape,
): Graphic => {
  const geometry = new Point(json);
  const symbol = symbolPoint(color, style);
  return new Graphic({ geometry, symbol, attributes: { shape } });
};

// When working with Graphics, a `Polyline`
// is equivilent to a GeoJSON `LineString`
export const graphicLineString = (
  json: ArcgisJSON,
  color: string,
  style?: GraphicStyle,
  shape?: GeometricShape,
): Graphic => {
  const geometry = new Polyline(json);
  const symbol = symbolLineString(color, style);
  return new Graphic({ geometry, symbol, attributes: { shape } });
};

export const graphicPolygon = (
  json: ArcgisJSON,
  color: string,
  style?: GraphicStyle,
  shape?: GeometricShape,
): Graphic => {
  const geometry = new Polygon(json);
  const symbol = symbolPolygon(color, style);
  return new Graphic({ geometry, symbol, attributes: { shape } });
};

export const polygonFromExtent = (extent: Extent): Graphic => {
  const geometry = Polygon.fromExtent(extent);
  const symbol = symbolPolygon(DEFAULT_COLOR);

  return new Graphic({ geometry, symbol });
};
